import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SurveyService } from '../../../services/surveys.service';
import { ToastService } from '../../../services/general/toast.service';

interface SpeechToTextResult {
  transcript: string;
  attachment?: any;
}

type TranscriptionStatus = 'IDLE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

type AudioProcessingStep = 'preparing' | 'uploading' | 'transcribing';

@Component({
  selector: 'app-speech-to-text-recorder',
  templateUrl: './speech-to-text-recorder.component.html',
  styleUrls: ['./speech-to-text-recorder.component.scss'],
  standalone: false,
})
export class SpeechToTextRecorderComponent implements OnInit, OnDestroy {
  @Input() surveyId: string | number;
  @Input() questionId: string | number;
  @Input() disabled = false;
  @Input() language = 'it-IT';
  @Input() targetField: 'answer' | 'feedback' = 'answer';
  @Input() maxDurationSeconds = 300;
  @Input() isMedical = false;

  @Output() transcriptionCompleted = new EventEmitter<SpeechToTextResult>();
  @Output() audioAttachmentCreated = new EventEmitter<any>();
  @Output() processingStateChanged = new EventEmitter<boolean>();

  isSupported = false;
  isRecording = false;
  isUploading = false;
  permissionDenied = false;
  elapsedSeconds = 0;
  micLevelPercent = 0;
  readonly micMeterSegments = Array.from({ length: 14 }, (_, i) => i);
  errorMessage = '';
  transcriptionStatus: TranscriptionStatus = 'IDLE';
  uploadFailed = false;
  /** Shown with spinner: preparing file → POST upload → polling transcription */
  processingStep: AudioProcessingStep = 'uploading';

  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private timerId: ReturnType<typeof setInterval> | null = null;
  private pollingTimerId: ReturnType<typeof setInterval> | null = null;
  private shouldUploadRecording = true;
  private readonly pollingIntervalMs = 5000;
  private readonly pollingTimeoutMs = 90000;
  private pollingStartedAt = 0;
  private currentTranscriptionId: string | null = null;
  private currentAudioAttachment: any = null;
  private lastRecordedAudioFile: File | null = null;
  private smoothedMicLevel = 0;
  private noiseFloor = 0.02;
  private audioContext: AudioContext | null = null;
  private analyserNode: AnalyserNode | null = null;
  private micDataArray: Uint8Array | null = null;
  private micAnimationFrameId: number | null = null;

  constructor(
    private surveyService: SurveyService,
    private toast: ToastService,
    private translateService: TranslateService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.isSupported = !!(
      navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined'
    );
    this.checkMicrophonePermission();
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.clearPolling();
    this.stopTracks();
    this.stopMicLevelMonitoring();
  }

  async startRecording(): Promise<void> {
    if (this.disabled || this.isRecording || this.isUploading) return;

    this.errorMessage = '';
    this.permissionDenied = false;
    this.shouldUploadRecording = true;
    this.transcriptionStatus = 'IDLE';
    this.currentTranscriptionId = null;
    this.currentAudioAttachment = null;
    this.lastRecordedAudioFile = null;
    this.uploadFailed = false;
    this.clearPolling();

    if (!this.isSupported) {
      this.showError('components.surveys.speech-to-text.unsupported-browser');
      return;
    }

    if (!this.surveyId || !this.questionId) {
      this.showError('components.surveys.speech-to-text.missing-question');
      return;
    }

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      const mimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(
        this.mediaStream,
        mimeType ? { mimeType } : undefined,
      );
      this.chunks = [];

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data?.size > 0) {
          this.chunks.push(event.data);
        }
      };

      this.mediaRecorder.onerror = () => {
        this.showError('components.surveys.speech-to-text.recording-error');
        this.stopRecording();
      };

      this.mediaRecorder.onstop = () => {
        this.handleRecordingStopped();
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      this.processingStateChanged.emit(true);
      this.startMicLevelMonitoring();
      this.elapsedSeconds = 0;
      this.startTimer();
    } catch (error: any) {
      this.handleMicrophoneError(error);
    }
  }

  stopRecording(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
      this.isRecording = false;
      this.clearTimer();
      this.stopTracks();
      this.processingStateChanged.emit(false);
      return;
    }

    this.isRecording = false;
    this.clearTimer();
    this.mediaRecorder.stop();
  }

  cancelRecording(): void {
    this.shouldUploadRecording = false;
    this.stopRecording();
    this.chunks = [];
    this.errorMessage = '';
    this.uploadFailed = false;
    this.lastRecordedAudioFile = null;
  }

  retryUpload(): void {
    if (!this.lastRecordedAudioFile || this.isUploading || this.isRecording) return;

    this.uploadFailed = false;
    this.errorMessage = '';
    this.transcribeAudio(this.lastRecordedAudioFile);
  }

  get formattedElapsed(): string {
    const minutes = Math.floor(this.elapsedSeconds / 60);
    const seconds = this.elapsedSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  get formattedMaxDuration(): string {
    const minutes = Math.floor(this.maxDurationSeconds / 60);
    const seconds = this.maxDurationSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  get processingMessageKey(): string {
    switch (this.processingStep) {
      case 'preparing':
        return 'components.surveys.speech-to-text.preparing-upload';
      case 'uploading':
        return 'components.surveys.speech-to-text.uploading-audio';
      default:
        return 'components.surveys.speech-to-text.transcribing';
    }
  }

  get activeMicSegments(): number {
    return Math.round((this.micLevelPercent / 100) * this.micMeterSegments.length);
  }

  getMicSegmentClass(index: number): string {
    const normalized = (index + 1) / this.micMeterSegments.length;
    if (normalized > 0.8) return 'danger';
    if (normalized > 0.6) return 'warning';
    return 'ok';
  }

  private async checkMicrophonePermission(): Promise<void> {
    if (!navigator.permissions?.query) return;

    try {
      const status = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });
      this.permissionDenied = status.state === 'denied';
      status.onchange = () => {
        this.permissionDenied = status.state === 'denied';
        this.cd.markForCheck();
      };
    } catch {
      // Some browsers do not expose microphone permission through Permissions API.
    }
  }

  private startTimer(): void {
    this.clearTimer();
    this.timerId = setInterval(() => {
      this.elapsedSeconds += 1;
      if (this.elapsedSeconds >= this.maxDurationSeconds) {
        this.stopRecording();
      }
      this.cd.markForCheck();
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private handleRecordingStopped(): void {
    this.stopTracks();
    this.stopMicLevelMonitoring();

    if (!this.shouldUploadRecording) {
      this.processingStateChanged.emit(false);
      this.cd.markForCheck();
      return;
    }

    this.isUploading = true;
    this.processingStep = 'preparing';
    this.transcriptionStatus = 'IN_PROGRESS';
    this.errorMessage = '';
    this.uploadFailed = false;
    this.cd.markForCheck();

    const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
    const audioBlob = new Blob(this.chunks, { type: mimeType });
    this.chunks = [];

    if (!audioBlob.size) {
      this.isUploading = false;
      this.transcriptionStatus = 'IDLE';
      this.processingStateChanged.emit(false);
      this.showError('components.surveys.speech-to-text.empty-recording');
      return;
    }

    const audioFile = new File(
      [audioBlob],
      `voice-answer-${new Date().toISOString()}.${this.getFileExtension(mimeType)}`,
      { type: mimeType },
    );

    this.lastRecordedAudioFile = audioFile;
    this.transcribeAudio(audioFile);
  }

  private transcribeAudio(audioFile: File): void {
    this.isUploading = true;
    this.processingStep = 'uploading';
    this.transcriptionStatus = 'IN_PROGRESS';
    this.errorMessage = '';
    this.uploadFailed = false;
    this.cd.markForCheck();

    this.surveyService.transcribeSurveyQuestionAnswerAudio(
      this.surveyId.toString(),
      this.questionId.toString(),
      audioFile,
      this.targetField,
      this.language,
      (result) => {
        this.handleTranscriptionStartResponse(result);
      },
      () => {
        this.isUploading = false;
        this.transcriptionStatus = 'FAILED';
        this.processingStateChanged.emit(false);
        this.uploadFailed = true;
        this.errorMessage = this.translateService.instant(
          'components.surveys.speech-to-text.upload-failed',
        );
        this.cd.markForCheck();
      },
      this.isMedical,
    );
  }

  private handleTranscriptionStartResponse(result: any): void {
    this.currentTranscriptionId = result?.transcription_id?.toString() || null;
    this.currentAudioAttachment =
      result?.audio_attachment || result?.attachment || result?.file || null;

    if (this.currentAudioAttachment) {
      this.audioAttachmentCreated.emit(this.currentAudioAttachment);
    }

    if (result?.status === 'COMPLETED' && (result?.transcript || result?.text)) {
      this.handleTranscriptionCompleted(result);
      return;
    }

    if (result?.status === 'FAILED') {
      this.handleTranscriptionFailed(result?.failure_reason);
      return;
    }

    if (!this.currentTranscriptionId) {
      this.handleTranscriptionFailed();
      return;
    }

    this.transcriptionStatus = 'IN_PROGRESS';
    this.processingStep = 'transcribing';
    this.startPollingTranscriptionStatus();
    this.cd.markForCheck();
  }

  private startPollingTranscriptionStatus(): void {
    this.clearPolling();
    this.pollingStartedAt = Date.now();

    this.pollingTimerId = setInterval(() => {
      if (Date.now() - this.pollingStartedAt >= this.pollingTimeoutMs) {
        this.clearPolling();
        this.isUploading = false;
        this.processingStateChanged.emit(false);
        this.transcriptionStatus = 'FAILED';
        this.errorMessage = this.translateService.instant(
          'components.surveys.speech-to-text.transcription-timeout',
        );
        this.toast.alert(
          this.translateService.instant('generic.warning'),
          this.errorMessage,
        );
        this.cd.markForCheck();
        return;
      }

      this.pollTranscriptionStatus();
    }, this.pollingIntervalMs);
  }

  private pollTranscriptionStatus(): void {
    if (!this.currentTranscriptionId) return;

    this.surveyService.getSurveyQuestionAnswerAudioTranscriptionStatus(
      this.surveyId.toString(),
      this.questionId.toString(),
      this.currentTranscriptionId,
      (result) => {
        if (result?.audio_attachment || result?.attachment || result?.file) {
          this.currentAudioAttachment =
            result.audio_attachment || result.attachment || result.file;
          this.audioAttachmentCreated.emit(this.currentAudioAttachment);
        }

        if (result?.status === 'COMPLETED') {
          this.handleTranscriptionCompleted(result);
          return;
        }

        if (result?.status === 'FAILED') {
          this.handleTranscriptionFailed(result?.failure_reason);
        }
      },
      () => {
        this.handleTranscriptionFailed();
      },
    );
  }

  private handleTranscriptionCompleted(result: any): void {
    const transcript = result?.transcript || result?.text || '';
    const attachment =
      result?.audio_attachment ||
      result?.attachment ||
      result?.file ||
      this.currentAudioAttachment;

    this.clearPolling();
    this.isUploading = false;
    this.processingStateChanged.emit(false);

    if (!transcript) {
      this.handleTranscriptionFailed();
      return;
    }

    this.transcriptionStatus = 'COMPLETED';
    this.uploadFailed = false;
    this.lastRecordedAudioFile = null;
    this.errorMessage = '';
    this.transcriptionCompleted.emit({
      transcript,
      attachment,
    });
    this.toast.success(
      this.translateService.instant('generic.success'),
      this.translateService.instant(
        'components.surveys.speech-to-text.transcription-completed',
      ),
    );
    this.cd.markForCheck();
  }

  private handleTranscriptionFailed(failureReason?: string): void {
    this.clearPolling();
    this.isUploading = false;
    this.processingStateChanged.emit(false);
    this.transcriptionStatus = 'FAILED';
    console.error('Speech-to-text transcription failed', failureReason);
    this.errorMessage = this.translateService.instant(
      'components.surveys.speech-to-text.transcription-failed',
    );
    this.toast.alert(
      this.translateService.instant('generic.warning'),
      this.errorMessage,
    );
    this.cd.markForCheck();
  }

  private clearPolling(): void {
    if (this.pollingTimerId) {
      clearInterval(this.pollingTimerId);
      this.pollingTimerId = null;
    }
  }

  private handleMicrophoneError(error: any): void {
    const errorName = error?.name;

    if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
      this.permissionDenied = true;
      this.showError('components.surveys.speech-to-text.microphone-permission-denied');
      return;
    }

    if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
      this.showError('components.surveys.speech-to-text.microphone-not-found');
      return;
    }

    this.showError('components.surveys.speech-to-text.microphone-unavailable');
  }

  private stopTracks(): void {
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = null;
  }

  private startMicLevelMonitoring(): void {
    if (!this.mediaStream) return;
    this.stopMicLevelMonitoring();
    try {
      this.audioContext = new AudioContext();
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.analyserNode = this.audioContext.createAnalyser();
      this.analyserNode.fftSize = 256;
      source.connect(this.analyserNode);
      this.micDataArray = new Uint8Array(this.analyserNode.frequencyBinCount);

      const tick = () => {
        if (!this.analyserNode || !this.micDataArray || !this.isRecording) return;
        this.analyserNode.getByteFrequencyData(this.micDataArray as any);
        let sum = 0;
        let peak = 0;
        for (let i = 0; i < this.micDataArray.length; i += 1) {
          const value = this.micDataArray[i];
          sum += value;
          if (value > peak) peak = value;
        }
        const avg = sum / this.micDataArray.length;
        const peakNorm = peak / 255;
        const avgNorm = avg / 255;

        // Adaptive noise floor: tracks ambient background slowly.
        const quietSample = avgNorm * 0.7 + peakNorm * 0.3;
        this.noiseFloor =
          this.noiseFloor * 0.995 + Math.min(quietSample, this.noiseFloor + 0.002) * 0.005;

        // Remove baseline noise and remap to 0..1 so silence goes close to zero.
        const raw = Math.max(peakNorm * 0.85, avgNorm * 1.35);
        const gated = Math.max(
          0,
          (raw - (this.noiseFloor + 0.015)) / Math.max(0.15, 1 - (this.noiseFloor + 0.015)),
        );
        const shaped = Math.pow(Math.max(0, Math.min(1, gated)), 0.9) * 100;

        const attack = 0.55;
        const decay = 0.12;
        const alpha = shaped > this.smoothedMicLevel ? attack : decay;
        this.smoothedMicLevel =
          this.smoothedMicLevel + (shaped - this.smoothedMicLevel) * alpha;
        this.micLevelPercent = Math.max(
          0,
          Math.min(100, Math.round(this.smoothedMicLevel)),
        );
        this.cd.markForCheck();
        this.micAnimationFrameId = requestAnimationFrame(tick);
      };
      this.micAnimationFrameId = requestAnimationFrame(tick);
    } catch {
      this.micLevelPercent = 0;
    }
  }

  private stopMicLevelMonitoring(): void {
    if (this.micAnimationFrameId != null) {
      cancelAnimationFrame(this.micAnimationFrameId);
      this.micAnimationFrameId = null;
    }
    this.micDataArray = null;
    this.analyserNode = null;
    this.smoothedMicLevel = 0;
    this.noiseFloor = 0.02;
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.micLevelPercent = 0;
  }

  private getSupportedMimeType(): string {
    const mimeTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];

    return (
      mimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ||
      ''
    );
  }

  private getFileExtension(mimeType: string): string {
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('mp4')) return 'm4a';
    if (mimeType.includes('wav')) return 'wav';
    return 'webm';
  }

  private showError(messageKey: string): void {
    const message = this.translateService.instant(messageKey);
    this.errorMessage = message;
    this.toast.alert(this.translateService.instant('generic.warning'), message);
    this.cd.markForCheck();
  }
}
